import { useMemo } from 'react';
import {
  Paper, Table, TableHead, TableRow, TableCell, TableBody, Box, Typography, Link, Chip, Tooltip,
} from '@mui/material';
import { EMPTY_MAPPING_TOOLTIP } from '../../utils/constants';

// FEAT-002 v0.3 — 관점 1: 제품 × 소프트웨어 (List + Badge 그룹).
// 좌측 컬럼 = 제품 식별자/이름/담당, 우측 컬럼 = 탑재 SW 의 Badge 묶음 (SW 이름 + latest 버전).
//
// 표현 방식 변경 사유 (v0.3): sparse 매트릭스 평면은 30~40 SW 가진 일부 제품 때문에
// 모든 행이 N-k 빈 셀로 채워지는 문제 — List 형식이 데이터 형식에 적합.
//
// Props:
//   products  : products.yaml 의 products 배열
//   softwares : softwares.yaml 의 repos (transformSoftwares 결과)
//   search    : 행 검색어 (제품 ID/이름)
export default function ProductSwMatrix({ products, softwares, search }) {
  // SW 메타 lookup (id → meta)
  const swMetaMap = useMemo(() => {
    const map = {};
    softwares.forEach((s) => { map[s.id] = s; });
    return map;
  }, [softwares]);

  // 검색 필터링 — 좌측 컬럼 (제품 식별자/이름) 부분 일치
  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) =>
      `${p.id} ${p.name || ''} ${p.owner || ''}`.toLowerCase().includes(q),
    );
  }, [products, search]);

  if (filteredProducts.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">검색 결과 없음</Typography>
      </Paper>
    );
  }

  return (
    <Paper>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: { xs: 200, md: 260 } }}>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>제품</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>구성 소프트웨어</Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredProducts.map((p) => {
            const swIds = p.softwares || [];
            return (
              <TableRow key={p.id} hover sx={{ verticalAlign: 'top' }}>
                <TableCell sx={{ py: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.name || p.id}</Typography>
                  <Link
                    href={`https://github.com/rayrndsw/${p.id}`}
                    target="_blank"
                    rel="noopener"
                    underline="hover"
                    sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: 'text.secondary' }}
                  >
                    {p.id}
                  </Link>
                  {p.owner && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                      {p.owner}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  {swIds.length === 0 ? (
                    <Tooltip title={EMPTY_MAPPING_TOOLTIP} arrow placement="top">
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.disabled',
                          fontStyle: 'italic',
                          cursor: 'help',
                          display: 'inline-block',
                          px: 1,
                          py: 0.5,
                          bgcolor: 'action.hover',
                          borderRadius: 1,
                        }}
                      >
                        ⓘ 매핑 미입력
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {swIds.map((swId) => {
                        const meta = swMetaMap[swId];
                        const ver = meta?.versions?.latest?.tag;
                        const pub = meta?.versions?.latest?.published;
                        const label = ver ? `${swId} ${ver}` : swId;
                        const tipBody = [
                          meta?.name ? `이름: ${meta.name}` : null,
                          ver ? `최신: ${ver}` : '최신 출시 정보 없음',
                          pub ? `출시일: ${pub}` : null,
                        ].filter(Boolean).join('\n');
                        return (
                          <Tooltip
                            key={swId}
                            title={<Box sx={{ whiteSpace: 'pre-line' }}>{tipBody}</Box>}
                            arrow
                            placement="top"
                          >
                            <Chip
                              label={label}
                              size="small"
                              variant="outlined"
                              component="a"
                              href={`https://github.com/rayrndsw/${swId}`}
                              target="_blank"
                              rel="noopener"
                              clickable
                              sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}
                            />
                          </Tooltip>
                        );
                      })}
                      <Typography variant="caption" sx={{ color: 'text.secondary', alignSelf: 'center', ml: 1 }}>
                        ({swIds.length}개)
                      </Typography>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );
}
